import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Table } from 'antd';
import { Header, Button } from '@blueprintnyc/bpnyc-common-admin';
import { Info } from '@blueprintnyc/bpnyc-common-admin/dist/icons';
import EventStatusIndicator from 'components/EventStatusIndicator';
import EventListItem from 'components/EventListItem';
import EventCreateDialog from 'components/EventCreateDialog';
import TooManyEventsDialog from 'components/TooManyEventsDialog';
import Search from 'components/Search';
import TopNavLayout from 'components/TopNavLayout';
import ProgressiveImage from 'components/ProgressiveImage';
import {
	clearAttendees,
	clearEventMedia,
	clearEventSyncs,
	clearAnnouncements,
	clearAgenda,
	clearKudoBadges,
	clearGroups,
} from '@/redux/actions';
import { isSaaSAdmin } from '@/redux/util';

import './EventsList.scss';

const pinnedEventIDs = [17, 2, 33, 127, 120];

const inputFormat = moment.ISO_8601;
const outputFormat = 'MM/DD/YYYY';

const columns = () => [
	{
		title: '',
		dataIndex: 'event_image_url',
		key: 'event_image_url',
		render: (text, record) => (
			<div className={`event-image ${record.event_image_url ? '' : 'bg'}`}>
				{record.event_image_url && (
					<ProgressiveImage
						src={record.event_image_url}
						alt={record.name || record.event_image_url}
						style={{ height: '60px', width: '60px', objectFit: 'cover', borderRadius: '5px' }}
					/>
				)}
			</div>
		),
	},
	{
		title: 'Name',
		dataIndex: 'name',
		key: 'name',
	},
	{
		title: 'Date',
		dataIndex: 'start_time',
		key: 'start_time',
		render: (text, record) => {
			const startTime = moment(record.start_time, inputFormat).format(outputFormat);
			const endTime = moment(record.end_time, inputFormat).format(outputFormat);
			return (
				<div className="time">
					{`${startTime}`}&nbsp;{`- ${endTime}`}
				</div>
			);
		},
	},
	{
		title: 'Location',
		dataIndex: 'location',
		key: 'location',
	},
	{
		title: 'Last Updated',
		dataIndex: 'updated_at',
		key: 'updated_at',
		render: (text, record) => <div>{moment(record.updated_at, moment.ISO_8601).fromNow()}</div>,
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		render: status => (
			<div className="event-status">
				<EventStatusIndicator status={status} />
			</div>
		),
	},
];

const EventsList = props => {
	const { events, numUnpaidEvents, client, canCreateEvent, auth0Token, socket, curtain } = props;
	const navigate = useNavigate();
	const isSaaS = isSaaSAdmin();
	const [showArchived, setShowArchived] = useState(false);
	const [createVisible, setCreateVisible] = useState(false);
	const [warningVisible, setWarningVisible] = useState(false);
	const [sortedEvents, setSortedEvents] = useState([]);
	const [eventsList, setEventsList] = useState([]);
	const [searchTerm, setSearchTerm] = useState(null);

	const showModal = () => {
		setCreateVisible(true);
	};

	const showWarningModal = () => {
		setWarningVisible(true);
	};

	const handleCancelWarning = () => {
		setWarningVisible(false);
	};

	const handleCancel = () => {
		setCreateVisible(false);
	};

	const toggleArchived = () => {
		setShowArchived(!showArchived);
	};

	const handleCreate = (event, redirect = true) => {
		setCreateVisible(false);
		if (redirect) navigate(`/events/${event.id}/settings`);
	};

	const handleSearch = e => {
		const newSearchTerm = e.target ? e.target.value : e;
		setSearchTerm(newSearchTerm);
	};

	const pinnedRank = eventID => {
		return pinnedEventIDs.includes(eventID) ? pinnedEventIDs.indexOf(eventID) : 999;
	};

	useEffect(() => {
		props.clearAttendees();
		props.clearEventSyncs();
		props.clearAnnouncements();
		props.clearEventMedia();
		props.clearAgenda();
		props.clearKudos();
		props.clearGroups();
	}, []);

	useEffect(() => {
		let sorted = events
			? events.sort((a, b) => {
					if (pinnedRank(a.id) !== pinnedRank(b.id)) {
						return pinnedRank(a.id) - pinnedRank(b.id);
					} else if (!a.deleted_at !== !b.deleted_at) {
						return a.deleted_at && !b.deleted_at ? 1 : -1;
					} else {
						return b.updated_at.localeCompare(a.updated_at);
					}
			  })
			: [];

		let el = sorted
			.filter(event => (!showArchived ? !event.deleted_at : true))
			.filter(
				event =>
					!searchTerm || !event.name || event.name.toLowerCase().includes(searchTerm.toLowerCase())
			);
		setSortedEvents(sorted);
		setEventsList(el);
	}, [events, showArchived, setSortedEvents, setEventsList, searchTerm]);

	const tooManyUnpaidEvents = numUnpaidEvents >= 2;

	const hasArchivedEvents = sortedEvents.filter(event => event.deleted_at).length;

	const showArchivedEventsToggle = hasArchivedEvents ? (
		<div className="show-archived" onClick={toggleArchived}>
			{showArchived ? 'Hide' : 'Show'} Archived Events
		</div>
	) : null;

	return (
		<TopNavLayout className="EventsList">
			<Header>
				<div className="left">
					<span className="title">{`${
						client.id && client.id !== 1 ? client.name : 'Your'
					} Events`}</span>
					<Search placeholder="Search Events" onSubmit={handleSearch} />
				</div>
				<div className="right">
					{isSaaS && (
						<div className="remaining">
							{`${2 - numUnpaidEvents}/2 Free events remaining`}
							<span className="remaining-info" onClick={showWarningModal}>
								<Info />
							</span>
						</div>
					)}
					{canCreateEvent && (
						<Button
							type="primary"
							onClick={isSaaS && tooManyUnpaidEvents ? showWarningModal : showModal}
						>
							Add New Event
						</Button>
					)}
				</div>
			</Header>
			<div className="events-list-container">
				<div className="events-list">
					{eventsList.slice(0, 4).map(event => (
						<EventListItem
							auth0Token={auth0Token}
							socket={socket}
							key={event.name + event.id}
							event={event}
						/>
					))}
				</div>
			</div>
			{eventsList.length > 4 && <div className="divider">More Events</div>}
			{eventsList.length > 4 && (
				<div className="events-list-table-container">
					<Table
						columns={columns()}
						dataSource={eventsList.slice(4)}
						rowKey="id"
						onRow={record => {
							return {
								onClick: () => navigate(`/events/${record.id}/dashboard`),
							};
						}}
						pagination={{
							defaultPageSize: 1000,
							hideOnSinglePage: true,
						}}
					/>
				</div>
			)}
			{showArchivedEventsToggle}
			<EventCreateDialog
				visible={createVisible}
				client={client}
				onCreate={handleCreate}
				onCancel={handleCancel}
			/>
			<TooManyEventsDialog visible={warningVisible} onCancel={handleCancelWarning} />
			{curtain && <div className="scrim" />}
			{curtain && <div className="curtain" />}
		</TopNavLayout>
	);
};

const msp = ({ auth, ws }) => ({
	auth0Token: auth.auth0Token,
	socket: ws.socket,
});

const mdp = dispatch => ({
	clearAttendees: () => dispatch(clearAttendees()),
	clearEventSyncs: () => dispatch(clearEventSyncs()),
	clearAnnouncements: () => dispatch(clearAnnouncements()),
	clearEventMedia: () => dispatch(clearEventMedia()),
	clearAgenda: () => dispatch(clearAgenda()),
	clearKudos: () => dispatch(clearKudoBadges()),
	clearGroups: () => dispatch(clearGroups()),
});
export default connect(msp, mdp)(EventsList);
